import React, { useState, useRef, useEffect } from 'react';
import templateAssistantService from '../services/templateAssistantService';

// Splits assistant text into ordered segments of plain prose and ```html code blocks.
function parseSegments(text) {
  const segments = [];
  const re = /```html\s*([\s\S]*?)```/gi;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: 'text', value: text.slice(last, m.index) });
    segments.push({ type: 'html', value: m[1].trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', value: text.slice(last) });
  return segments;
}

// target: 'header' | 'footer' | 'full' — scopes the assistant to one part of the template, or to the
// whole document. The 'full' case is the one that takes ATTACHMENTS: a hotel asking for its own folio
// layout already has the answer as a PDF, and reading that beats describing it in prose.
//
// `context` is merged into what the backend receives, so a caller can hand the assistant whatever its
// screen knows (the canonical payload, the current template) without this component learning about it.
// `height` lets a full-screen host give the transcript the room a document-length conversation needs.
//
// `assist(messages)` replaces the folio assistant's backend for another kind of template (the billing template editor
// passes billingService.templateAssistant): it resolves to { data: { reply } }. `intro` / `placeholder` are what the
// chat says before the first message and in its input box.
const TemplateAssistantChat = ({
  target, availableFields, currentHeader, currentFooter, onInsert,
  context = {}, allowAttachments = false, height = 200, title,
  assist = null, intro = null, placeholder = null,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const label = title || (target === 'footer' ? 'Footer Assistant'
    : target === 'full' ? 'Template Assistant' : 'Header Assistant');
  const subject = target === 'full' ? 'template' : target;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // A user message is a plain string until something is attached, at which point it becomes the block
  // list the API expects. Attachments ride on the message they were sent with, so the transcript keeps
  // showing the model what it was looking at when it answered.
  const send = async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || loading) return;
    const content = attachments.length
      ? [
          ...attachments.map(a => ({ type: 'attachment', mediaType: a.mediaType, data: a.data, name: a.name })),
          { type: 'text', text: text || 'Build a template that reproduces the attached document.' },
        ]
      : text;
    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setAttachments([]);
    setError(null);
    setLoading(true);
    try {
      // A full document is a job, not a request: it outlives API Gateway's 29-second limit. Anything
      // shorter is answered inline, where a poll loop would only add latency.
      const call = assist ? ((msgs) => assist(msgs))
        : target === 'full' ? templateAssistantService.chatJob : templateAssistantService.chat;
      const res = await call(next, {
        target,
        availableFields,
        currentHeader: currentHeader || '',
        currentFooter: currentFooter || '',
        ...context,
      });
      const reply = res.data?.reply || '(no response)';
      // Apply any HTML the assistant produced straight into the target field — no manual step.
      const htmlSegs = parseSegments(reply).filter(s => s.type === 'html');
      if (htmlSegs.length) onInsert(htmlSegs[htmlSegs.length - 1].value);
      setMessages([...next, { role: 'assistant', content: reply }]);
    } catch (err) {
      // A job reports its failure as a thrown Error (no HTTP response), an inline call as a response body.
      setError(err.response?.data?.error?.message || err.message || 'The assistant request failed.');
    } finally {
      setLoading(false);
    }
  };

  // 4 MB per file: API Gateway refuses a request body over 10 MB and base64 adds a third, so a bigger
  // PDF would fail with a network error that says nothing about size. Caught here, it is a sentence.
  const MAX_FILE_BYTES = 4 * 1024 * 1024;

  const addFiles = async (files) => {
    const picked = [];
    for (const file of Array.from(files || [])) {
      if (file.size > MAX_FILE_BYTES) {
        setError(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — attachments must be under 4 MB.`);
        continue;
      }
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        // readAsDataURL gives "data:<type>;base64,<data>" — the API wants only the second half.
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      picked.push({ name: file.name, mediaType: file.type || 'application/pdf', data });
    }
    if (picked.length) setError(null);
    setAttachments(prev => [...prev, ...picked]);
  };

  const messageText = (content) => (typeof content === 'string'
    ? content
    : (content || []).filter(b => b.type === 'text').map(b => b.text).join('\n'));
  const messageFiles = (content) => (typeof content === 'string'
    ? []
    : (content || []).filter(b => b.type === 'attachment'));

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4a6cf7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span style={{ fontSize: '0.825rem', fontWeight: 600, color: '#111827' }}>{label}</span>
      </div>

      <div ref={scrollRef} style={{ overflowY: 'auto', padding: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', height }}>
        {messages.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>
            {intro || (allowAttachments
              ? 'Attach a PDF of a folio this hotel already issues and I will read it, reproduce the layout, and map each value onto the canonical fields. Anything I cannot map, I will tell you.'
              : `Ask me to draft or tweak the ${subject}. I'll use the available fields, and you can insert the result with one click.`)}
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%' }}>
            <div style={{
              fontSize: '0.8rem', lineHeight: 1.5, padding: '0.45rem 0.65rem', borderRadius: 10,
              background: m.role === 'user' ? '#eef2ff' : '#f3f4f6',
              color: '#111827', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {m.role === 'assistant'
                ? parseSegments(m.content).map((seg, j) => seg.type === 'text'
                    ? <span key={j}>{seg.value}</span>
                    : (
                      <div key={j} style={{ margin: '0.5rem 0' }}>
                        <pre style={{ background: '#111827', color: '#e5e7eb', padding: '0.55rem', borderRadius: 7, fontSize: '0.7rem', overflowX: 'auto', margin: 0 }}>{seg.value}</pre>
                        <div style={{ marginTop: 6, fontSize: '0.72rem', fontWeight: 600, color: '#16a34a' }}>✓ Applied to {subject} HTML</div>
                      </div>
                    ))
                : (
                  <>
                    {messageFiles(m.content).map((f, j) => (
                      <div key={`f${j}`} style={{ fontSize: '0.75rem', color: '#4b5563', marginBottom: 4 }}>📎 {f.name}</div>
                    ))}
                    {messageText(m.content)}
                  </>
                )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
            {target === 'full' ? 'Reading the document and building the template — this takes a minute…' : 'Thinking…'}
          </div>
        )}
        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626' }}>{error}</div>}
      </div>

      {allowAttachments && attachments.length > 0 && (
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.5rem 0.55rem 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {attachments.map((a, i) => (
            <span key={i} style={{ fontSize: '0.75rem', background: '#eef2ff', color: '#1e40af', borderRadius: 6, padding: '0.2rem 0.45rem' }}>
              📎 {a.name}
              <button
                type="button"
                onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                style={{ marginLeft: 6, border: 'none', background: 'none', color: '#1e40af', cursor: 'pointer' }}
                title="Remove"
              >×</button>
            </span>
          ))}
        </div>
      )}

      <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.55rem', display: 'flex', gap: 8 }}>
        {allowAttachments && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/gif,image/webp"
              multiple
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              title="Attach a PDF or image of an existing document"
              style={{
                alignSelf: 'flex-end', padding: '0.45rem 0.6rem', background: '#fff', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: 7, fontSize: '0.9rem', cursor: 'pointer',
              }}
            >📎</button>
          </>
        )}
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || (allowAttachments
            ? 'Attach a folio PDF and ask for a template that reproduces it…'
            : `Describe the ${subject} you want…`)}
          rows={2}
          style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 7, padding: '0.45rem 0.55rem', fontSize: '0.8rem', fontFamily: 'inherit', outline: 'none' }}
        />
        {(() => {
          const canSend = !loading && (input.trim() || attachments.length > 0);
          return (
            <button type="button" onClick={send} disabled={!canSend} style={{
              alignSelf: 'flex-end', padding: '0.45rem 0.9rem', background: '#4a6cf7', color: '#fff', border: 'none',
              borderRadius: 7, fontSize: '0.825rem', fontWeight: 600, cursor: canSend ? 'pointer' : 'not-allowed',
              opacity: canSend ? 1 : 0.5,
            }}>Send</button>
          );
        })()}
      </div>
    </div>
  );
};

export default TemplateAssistantChat;
